// Formatting helpers — locale + currency aware (Phase 5).
//
// USD is the canonical storage currency. We convert to the user's display
// currency at render time using the FX rates fetched from /api/fx/rates.

const LOCALE_FOR_LANG = {
  en: "en-US",
  es: "es-ES",
  "pt-BR": "pt-BR",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  "zh-CN": "zh-CN",
  ja: "ja-JP",
  ar: "ar-AE",
  hi: "hi-IN",
};

export function localeFor(lang) {
  return LOCALE_FOR_LANG[lang] || "en-US";
}

const DEFAULT_FX = {
  USD: 1.0, EUR: 0.92, GBP: 0.78, CAD: 1.36, MXN: 17.2, BRL: 4.95,
  AED: 3.67, JPY: 156.0, INR: 83.4, CNY: 7.18, AUD: 1.52, CHF: 0.88,
};

export function convertUsd(amountUsd, targetCurrency, fxRates) {
  if (amountUsd === null || amountUsd === undefined || Number.isNaN(Number(amountUsd))) return null;
  const rates = fxRates || DEFAULT_FX;
  const rate = rates[(targetCurrency || "USD").toUpperCase()];
  if (!rate) return Number(amountUsd);
  return Number(amountUsd) * Number(rate);
}

/**
 * formatCurrency(value, opts?)
 *   value: number in USD (canonical)
 *   opts.currency: display currency (default 'USD')
 *   opts.lang: language code for locale formatting (default 'en')
 *   opts.fxRates: optional rates map keyed by 3-letter code (default static fallback)
 *   opts.cents: keep cents (default false)
 *   opts.showOriginal: when currency !== USD, also show USD original in parens
 */
export function formatCurrency(value, opts = {}) {
  // Back-compat: support legacy `{cents: true}` signature where only cents was passed.
  const {
    currency = "USD",
    lang = "en",
    fxRates = null,
    cents = false,
    showOriginal = false,
  } = opts || {};

  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  const cc = (currency || "USD").toUpperCase();
  const locale = localeFor(lang);

  const minFrac = cents ? 2 : 0;
  const maxFrac = cents ? 2 : 0;
  const fmt = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: cc,
    minimumFractionDigits: minFrac,
    maximumFractionDigits: maxFrac,
  });

  const converted = cc === "USD" ? n : convertUsd(n, cc, fxRates);
  const primary = fmt.format(converted);

  if (showOriginal && cc !== "USD") {
    const usdFmt = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: minFrac,
      maximumFractionDigits: maxFrac,
    });
    return `${usdFmt.format(n)} (≈ ${primary})`;
  }
  return primary;
}

export function formatPercent(value, { decimals = 1, wholeNumber = false, lang = "en" } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  const n = Number(value) * (wholeNumber ? 1 : 100);
  return new Intl.NumberFormat(localeFor(lang), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n) + "%";
}

export function formatScore(value) {
  if (value === null || value === undefined) return "—";
  return Math.round(Number(value)).toString();
}

export function formatNumber(value, decimals = 0, lang = "en") {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString(localeFor(lang), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatDscr(value) {
  if (value === null || value === undefined) return "N/A — no debt";
  return `${Number(value).toFixed(2)}x`;
}

export const PROPERTY_TYPE_LABELS = {
  single_family: "Single Family",
  condo: "Condo",
  townhouse: "Townhouse",
  multifamily_2_4: "Multifamily (2–4)",
  multifamily_5_plus: "Multifamily (5+)",
  commercial: "Commercial",
};

export function propertyTypeLabel(t) {
  return PROPERTY_TYPE_LABELS[t] || t;
}

// 12-currency list shown in Settings → display currency dropdown.
export const SUPPORTED_DISPLAY_CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "AED", name: "UAE Dirham" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "INR", name: "Indian Rupee" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CHF", name: "Swiss Franc" },
];
